import React, { FC, useContext, useMemo } from 'react';
import classNames from 'classnames';
import { Field, useFormState } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import RadioButtonField from 'components/RadioGroupField/RadioButtonField';
import { Link } from 'react-router-dom';
import LinkToIcon from 'icons/LinkTo.icon';
import {
  DATA_ROOM_FAQ_ACCESS,
  DataRoomFaqAccessGranularityData,
  FAQ_LEAD_STAGES,
} from '../../models/FAQ';
import DataRoomContext from '../../providers/DataRoom/DataRoom.context';
import CheckboxFieldGroup from 'components/CheckboxFieldGroup';
import CheckboxField from 'components/CheckboxFieldGroup/CheckboxField';
import { LEAD_STAGES } from 'models/Lead';
import {
  getDataRoomRoute,
  getSellingPortfolio,
  getUserFullNameOrEmail,
} from 'utils';
import { NEW_WINDOW_PROPS } from 'data/global';
import validations from 'validations';

import './FAQAccess.styles.scss';

type FAQAccessProps = {
  className?: string;
  showFAQAccess: boolean;
  data: DataRoomFaqAccessGranularityData;
};

const FAQAccess: FC<FAQAccessProps> = ({
  className,
  children,
  data,
  showFAQAccess,
}) => {
  const { t } = useTranslation();
  const {
    values: { faqAccess, accessDataRoomBuySideMemberIds },
  } = useFormState();
  const {
    dataRoom: {
      _id: dataRoomId,
      listing: { dealRoomId },
    },
  } = useContext(DataRoomContext);

  const buySideMembersHasValue = useMemo(
    () => Object.values(accessDataRoomBuySideMemberIds || {}).some(Boolean),
    [accessDataRoomBuySideMemberIds],
  );

  const hasBuySideMembers = useMemo(
    () => !!data?.buySideDataRoomMembers?.length,
    [data?.buySideDataRoomMembers?.length],
  );

  return (
    <div className={classNames('faq-access', className)}>
      {children}

      {showFAQAccess && (
        <>
          <p>{t('specifyWhoCanViewThisQA')}</p>

          <div className="faq-access__option">
            <Field
              name="faqAccess"
              type="radio"
              required
              value={DATA_ROOM_FAQ_ACCESS.ALL}
              label={t('allDataRoomMembers')}
              component={RadioButtonField}
            />

            <Link
              to={getDataRoomRoute(dataRoomId, 'members')}
              {...NEW_WINDOW_PROPS}
            >
              <LinkToIcon />
              {t('viewAllMembers')}
            </Link>
          </div>

          <div className="faq-access__option">
            <Field
              name="faqAccess"
              type="radio"
              required
              value={DATA_ROOM_FAQ_ACCESS.PARTICULAR_STAGE}
              label={t('allMembersAtStage')}
              component={RadioButtonField}
              validate={validations.required()}
            />

            <Link
              to={getSellingPortfolio(dealRoomId, 'leads')}
              {...NEW_WINDOW_PROPS}
            >
              <LinkToIcon />
              {t('viewCurrentLeads')}
            </Link>
          </div>

          {faqAccess === DATA_ROOM_FAQ_ACCESS.PARTICULAR_STAGE && (
            <CheckboxFieldGroup
              name="faqAccessStages"
              className="faq-access__stages"
              required
              options={Object.values(LEAD_STAGES).map((el) => {
                const label = FAQ_LEAD_STAGES[el];
                const stage = data?.particularStages?.[label];

                return {
                  value: label,
                  label: `${t(label)} (${stage?.length || 0})`,
                };
              })}
            />
          )}

          {hasBuySideMembers && (
            <Field
              name="faqAccess"
              type="radio"
              required
              value={DATA_ROOM_FAQ_ACCESS.SPECIFIC}
              label={t('other')}
              component={RadioButtonField}
            />
          )}

          {hasBuySideMembers && faqAccess === DATA_ROOM_FAQ_ACCESS.SPECIFIC && (
            <div className="faq-access__specific">
              {data?.buySideDataRoomMembers.map((member) => {
                const { _id, isLead } = member || {};

                return (
                  <div key={_id} className="faq-access__specific__option">
                    <Field
                      name={`accessDataRoomBuySideMemberIds.${_id}`}
                      type="checkbox"
                      label={getUserFullNameOrEmail(member)}
                      component={CheckboxField}
                    />

                    {!isLead && <span>{t('external')}</span>}
                  </div>
                );
              })}

              {!buySideMembersHasValue && (
                <Field
                  name="accessDataRoomBuySideMemberIds"
                  hidden
                  component="input"
                  validate={() => t('requiredField')}
                />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FAQAccess;
